import React, { useState, useEffect } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { ArrowLeft, Loader2, Upload, X, Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';

const propertySchema = z.object({
    name: z.string().min(1, 'Name is required').max(100),
    description: z.string().max(500).optional(),
    category: z.string().min(1, 'Category is required'),
    address: z.string().min(1, 'Address is required'),
    units: z.array(z.object({
        unit_number: z.string().min(1, 'Required'),
        rent_amount: z.number().min(0, 'Required'),
        bedrooms: z.number().min(0).or(z.nan()), // Allow NaN for empty number inputs
        bathrooms: z.number().min(0).or(z.nan()),
    })).default([]),
});

type PropertyFormData = z.infer<typeof propertySchema>;

export const PropertyForm: React.FC = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const { refreshProfile } = useAuth();
    const { createProperty, updateProperty, properties, fetchPropertyDetails, currentProperty, loading, createUnit } = useLandlordStore();
    const isEditMode = !!propertyId;
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { register, control, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: zodResolver(propertySchema),
        defaultValues: {
            category: 'Residential',
            units: [{ unit_number: '', rent_amount: 0, bedrooms: 1, bathrooms: 1 }]
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "units"
    });

    useEffect(() => {
        if (isEditMode && propertyId) {
            const property = properties.find((p) => p.id === propertyId) || currentProperty;

            if (property && property.id === propertyId) {
                reset({
                    name: property.name,
                    description: property.description,
                    category: property.category,
                    address: property.address,
                    units: [] // For now, we don't load existing units into the edit form to complicate things
                });
                if (property.images?.length) setPreviewUrls(property.images);
            } else {
                fetchPropertyDetails(propertyId);
            }
        }
    }, [isEditMode, propertyId, properties, currentProperty, reset]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newPreviews = Array.from(e.target.files).map((f) => URL.createObjectURL(f));
            setPreviewUrls((prev) => [...prev, ...newPreviews]);
        }
    };

    const removeImage = (index: number) => {
        setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    };

    const onSubmit = async (data: PropertyFormData) => {
        setIsSubmitting(true);
        try {
            const payload = {
                name: data.name,
                description: data.description || '',
                category: data.category,
                address: data.address,
                images: previewUrls,
            };

            let targetPropertyId = propertyId;

            if (isEditMode && propertyId) {
                await updateProperty(propertyId, payload);
            } else {
                targetPropertyId = await createProperty(payload);
            }

            // Create units if any
            if (data.units && data.units.length > 0 && targetPropertyId) {
                for (const unit of data.units) {
                    if (unit.unit_number) {
                        await createUnit(targetPropertyId, {
                            ...unit,
                            rent_amount: unit.rent_amount || 0,
                            bedrooms: unit.bedrooms || 1, // Handle NaN or 0
                            bathrooms: unit.bathrooms || 1,
                            status: 'Available'
                        });
                    }
                }
            }

            await refreshProfile();
            navigate('/landlord/properties');
        } catch (error) {
            console.error('Failed to save property:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = "w-full px-4 py-2.5 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder:text-light-subtle dark:text-dark-subtle focus:outline-none focus:ring-2 focus:ring-ring transition-shadow";
    const labelClass = "block text-sm font-medium text-light-text dark:text-dark-text mb-1.5";

    return (
        <div className="max-w-4xl mx-auto pb-10">
            <div className="flex items-center gap-4 mb-6">
                <button onClick={() => navigate('/landlord/properties')} className="p-2 rounded-lg hover:bg-gray-100 dark:bg-gray-800 transition-colors">
                    <ArrowLeft size={22} className="text-light-subtle dark:text-dark-subtle" />
                </button>
                <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">{isEditMode ? 'Edit Property' : 'New Property'}</h1>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Property Details */}
                <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl border border-light-border dark:border-dark-border space-y-4 shadow-sm">
                    <h2 className="text-lg font-semibold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">Property Details</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className={labelClass}>Property Name</label>
                            <input {...register('name')} className={inputClass} placeholder="e.g., Sunset Apartments" />
                            {errors.name && <p className="text-danger dark:text-danger-dark text-xs mt-1">{errors.name.message}</p>}
                        </div>
                        <div>
                            <label className={labelClass}>Category</label>
                            <select {...register('category')} className={inputClass}>
                                <option value="Residential">Residential</option>
                                <option value="Commercial">Commercial</option>
                                <option value="Industrial">Industrial</option>
                                <option value="Land">Land</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>Address / Location</label>
                        <input {...register('address')} className={inputClass} placeholder="e.g., 123 Main St, Nairobi" />
                        {errors.address && <p className="text-danger dark:text-danger-dark text-xs mt-1">{errors.address.message}</p>}
                    </div>
                    <div>
                        <label className={labelClass}>Description</label>
                        <textarea {...register('description')} rows={3} className={inputClass} placeholder="Property description..." />
                    </div>
                </div>

                {/* Units Section - Only for New Properties for now to avoid complexity without ID */}
                {!isEditMode && (
                    <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl border border-light-border dark:border-dark-border space-y-4 shadow-sm">
                        <div className="flex items-center justify-between border-b border-light-border dark:border-dark-border pb-2">
                            <h2 className="text-lg font-semibold text-light-text dark:text-dark-text">Initial Units</h2>
                            <button
                                type="button"
                                onClick={() => append({ unit_number: '', rent_amount: 0, bedrooms: 1, bathrooms: 1 })}
                                className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                            >
                                <Plus size={16} /> Add Unit
                            </button>
                        </div>

                        <div className="space-y-3">
                            {fields.map((field, index) => (
                                <div key={field.id} className="flex gap-3 items-start p-3 bg-gray-100 dark:bg-gray-800/30 rounded-lg border border-light-border dark:border-dark-border/50">
                                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div>
                                            <input {...register(`units.${index}.unit_number` as const)} className={inputClass} placeholder="Unit No. (e.g. A1)" />
                                        </div>
                                        <div>
                                            <input
                                                type="number"
                                                {...register(`units.${index}.rent_amount` as const, { valueAsNumber: true })}
                                                className={inputClass}
                                                placeholder="Rent Amount"
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="number"
                                                {...register(`units.${index}.bedrooms` as const, { valueAsNumber: true })}
                                                className={inputClass}
                                                placeholder="Beds"
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="number"
                                                {...register(`units.${index}.bathrooms` as const, { valueAsNumber: true })}
                                                className={inputClass}
                                                placeholder="Baths"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="p-2 text-light-subtle dark:text-dark-subtle hover:text-danger dark:text-danger-dark transition-colors"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                            {fields.length === 0 && (
                                <p className="text-sm text-light-subtle dark:text-dark-subtle text-center py-4">No units added yet. You can add them later.</p>
                            )}
                        </div>
                        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800/30 text-xs text-amber-800 dark:text-amber-200">
                            Note: Units will be created after the property is saved.
                        </div>
                    </div>
                )}

                {/* Images */}
                <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl border border-light-border dark:border-dark-border space-y-4 shadow-sm">
                    <h2 className="text-lg font-semibold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">Images</h2>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {previewUrls.map((url, index) => (
                            <div key={index} className="relative aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden group">
                                <img src={url} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                                <button type="button" onClick={() => removeImage(index)} className="absolute top-1.5 right-1.5 p-1 bg-destructive text-danger dark:text-danger-dark-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                        <label className="flex flex-col items-center justify-center aspect-square bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-light-border dark:border-dark-border rounded-lg cursor-pointer hover:bg-gray-100 dark:bg-gray-800 transition-colors hover:border-primary/50">
                            <Upload size={20} className="text-light-subtle dark:text-dark-subtle mb-1" />
                            <span className="text-xs text-light-subtle dark:text-dark-subtle">Add Image</span>
                            <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageChange} />
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={() => navigate('/landlord/properties')} className="px-6 py-2.5 border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text font-medium hover:bg-gray-100 dark:bg-gray-800 transition-colors">
                        Cancel
                    </button>
                    <button type="submit" disabled={isSubmitting || loading} className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 disabled:opacity-50 shadow-lg shadow-primary/25 transition-all">
                        {(isSubmitting || loading) && <Loader2 size={18} className="animate-spin" />}
                        {isEditMode ? 'Update Property' : 'Create Property'}
                    </button>
                </div>
            </form>
        </div>
    );
};
